import { SubscriptionFreezeStatus } from 'proto/billing-manager_pb';
import { FREEZE_STEP_ORDER, FREEZE_SWITCH_OFF_STEP } from './content';
import { BILLING_PATH, FREEZE_PATH, FREEZE_RESUME_PATH, FREEZE_STATUS_PATH, PAYMENT_METHODS_PATH } from './paths';
import { SubscriptionFreeze } from './types';

/** Whether a saga at currentStep has reached target in order. An unknown or empty step, a saga that has not
 *  reported yet, reaches nothing. */
export function stepReached(order: string[], currentStep: string, target: string) {
  const reached = order.indexOf(currentStep);
  return reached >= 0 && reached >= order.indexOf(target);
}

/** Whether a pause at currentStep may have switched the customer off. */
export const freezeSwitchOffReached = (currentStep: string) => stepReached(FREEZE_STEP_ORDER, currentStep, FREEZE_SWITCH_OFF_STEP);

// The plans are paused and the customer switched off, whatever the step.
const pausedStatuses = new Set<number>([
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FROZEN,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUME_PAYMENT_PENDING,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_CANCELING
]);

// Still on the way in: switched off only once the switch-off step finished.
const switchingOff = new Set<number>([
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FREEZING,
  SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FREEZE_REPAIR_REQUIRED
]);

const SWITCHED_OFF_STEP = 'runtime_blocked';

/** Whether the pause has taken the customer's paid access away, so a token issued before still claims it. */
export function freezeSwitchedOff(freeze: Pick<SubscriptionFreeze, 'status' | 'currentStep'>) {
  if (pausedStatuses.has(freeze.status)) return true;
  return switchingOff.has(freeze.status) && stepReached(FREEZE_STEP_ORDER, freeze.currentStep, SWITCHED_OFF_STEP);
}

/** Where a customer with an open pause goes to come back: resume once it is in effect, else its status. */
export const freezeReturnPath = (freeze: Pick<SubscriptionFreeze, 'status'>) =>
  freeze.status === SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FROZEN ? FREEZE_RESUME_PATH : FREEZE_STATUS_PATH;

/** The pause's own pages. */
export const freezeOwnsPath = (pathname: string) => [FREEZE_PATH, FREEZE_RESUME_PATH].some((prefix) => pathname.startsWith(prefix));

/** Whether an open pause sends the customer from pathname to its status page. Only billing does: billing
 *  changes are refused while a pause is open. The rest of the app stays open, as for a free user. */
export function freezeLocksPath(pathname: string) {
  // Payment methods stays open: a declined resume is paid with another card.
  if (freezeOwnsPath(pathname) || pathname.startsWith(PAYMENT_METHODS_PATH)) return false;
  return pathname.startsWith(BILLING_PATH);
}
